import { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { apiRequest } from '../api/client';
import { backendConfig } from '../aws-config';
import { useAuth } from '../auth/AuthContext';

// Profile pictures are served by the API; the version in the URL changes on every upload, so caching is safe.
// A room's cover photo, versioned like profile pictures.
export const coverUrl = (sessionId?: string | null, version?: string | null) => (sessionId && version ? `${backendConfig.apiUrl}/covers/${sessionId}?v=${version}` : null);

// Crop a chosen photo to a centred 16:9 banner and shrink it to 1200x675.
export const bannerJpeg = (file: File) => new Promise<string>((resolve, reject) => {
  const image = new Image();
  image.onload = () => {
    const ratio = 16 / 9;
    const width = Math.min(image.width, image.height * ratio), height = width / ratio;
    const canvas = document.createElement('canvas');
    canvas.width = 1200; canvas.height = 675;
    canvas.getContext('2d')!.drawImage(image, (image.width - width) / 2, (image.height - height) / 2, width, height, 0, 0, 1200, 675);
    URL.revokeObjectURL(image.src);
    resolve(canvas.toDataURL('image/jpeg', 0.82));
  };
  image.onerror = () => reject(new Error('That file isn’t an image we can read.'));
  image.src = URL.createObjectURL(file);
});

export const avatarUrl = (userId?: string | null, version?: string | null) => (userId && version ? `${backendConfig.apiUrl}/avatars/${userId}?v=${version}` : null);

// Shrink a chosen photo to a centred 320px square JPEG before upload (keeps uploads around 20-40KB).
export const squareJpeg = (file: File, size = 320) => new Promise<string>((resolve, reject) => {
  const image = new Image();
  image.onload = () => {
    const side = Math.min(image.width, image.height);
    const canvas = document.createElement('canvas');
    canvas.width = size; canvas.height = size;
    canvas.getContext('2d')!.drawImage(image, (image.width - side) / 2, (image.height - side) / 2, side, side, 0, 0, size, size);
    URL.revokeObjectURL(image.src);
    resolve(canvas.toDataURL('image/jpeg', 0.85));
  };
  image.onerror = () => reject(new Error('That file isn’t an image we can read.'));
  image.src = URL.createObjectURL(file);
});

type Value = { avatarVersion: string | null; myAvatarUrl: string | null; upload: (file: File) => Promise<void>; remove: () => Promise<void> };
const Context = createContext<Value>({ avatarVersion: null, myAvatarUrl: null, upload: async () => undefined, remove: async () => undefined });

export function ProfileProvider({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  const [avatarVersion, setAvatarVersion] = useState<string | null>(null);
  useEffect(() => {
    if (!user) { setAvatarVersion(null); return; }
    apiRequest<any>('/me').then(profile => setAvatarVersion(profile?.avatarVersion || null)).catch(() => undefined);
  }, [user]);
  const upload = useCallback(async (file: File) => {
    const image = await squareJpeg(file);
    const result = await apiRequest<any>('/me/avatar', { method: 'PUT', body: JSON.stringify({ image }) });
    setAvatarVersion(result.avatarVersion);
  }, []);
  const remove = useCallback(async () => { await apiRequest('/me/avatar', { method: 'DELETE' }); setAvatarVersion(null); }, []);
  return <Context.Provider value={{ avatarVersion, myAvatarUrl: avatarUrl(user?.userId, avatarVersion), upload, remove }}>{children}</Context.Provider>;
}

export const useProfile = () => useContext(Context);
